import { useEffect, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  RotateCw,
  Save,
  Plus,
  X,
  Settings2,
  Cpu,
  FileText,
  Mail,
  Layers,
  Code,
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { FormField, FormInput, FormSelect, FormTextarea } from '@/components/form';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { scrollToFirstError, mergeApiErrors } from '@/lib/form-utils';

import { convertFieldsToSchema, convertSchemaToFields } from './review-field-schema';
import { ReviewFieldsSchemaEditor, type ReviewFieldDefinition } from './ReviewFieldsSchemaEditor';

import type { ReviewConfigFormProps } from './types';
import type { CreateReviewConfigDto } from '@repo/shared-types';

const GEMINI_MODELS = [
  { value: 'gemini-3.1-pro', label: 'Gemini 3.1 Pro — Most powerful' },
  { value: 'gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro Preview — Latest preview' },
  { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro — Best reasoning' },
  { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash — Fast + capable' },
  { value: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite — Cheapest' },
] as const;

const REVIEW_INPUT_MODES = [
  { value: 'TRANSCRIPT', label: 'Transcript (enriched post-call)' },
  { value: 'AUDIO', label: 'Audio (Gemini audio-first)' },
  { value: 'ORIGINAL_TRANSCRIPT', label: 'Original transcript (live agent, no enrichment)' },
] as const;

const TRANSCRIPT_LANGUAGES = [
  { value: '', label: 'Off — keep original language' },
  { value: 'en', label: 'English' },
  { value: 'ar', label: 'Arabic' },
  { value: 'de', label: 'German' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'it', label: 'Italian' },
  { value: 'nl', label: 'Dutch' },
  { value: 'pl', label: 'Polish' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'ro', label: 'Romanian' },
  { value: 'tr', label: 'Turkish' },
  { value: 'ur', label: 'Urdu' },
  { value: 'hi', label: 'Hindi' },
  { value: 'zh', label: 'Chinese' },
] as const;

// Form validation schema
const reviewConfigFormSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  prompt: z.string().min(10, 'Prompt must be at least 10 characters'),
  max_retries: z.number().int().min(1, 'Minimum is 1').max(100, 'Maximum is 100'),
  model_name: z.string().min(1),
  review_input_mode: z.enum(['TRANSCRIPT', 'AUDIO', 'ORIGINAL_TRANSCRIPT']),
  temperature: z.number().min(0, 'Minimum is 0').max(2, 'Maximum is 2'),
  is_default: z.boolean(),
  notification_emails: z.array(z.string().email()),
  high_intent_emails: z.array(z.string().email()),
  review_date: z.string().date().nullable().optional(),
  transcript_language: z.string().nullable().optional(),
  translation_model: z.string().min(1),
});

type ReviewConfigFormValues = z.infer<typeof reviewConfigFormSchema>;

export const ReviewConfigForm = ({
  initialValues,
  onSubmit,
  errors: apiErrors = [],
  isLoading = false,
  submitLabel = 'Save configuration',
}: ReviewConfigFormProps) => {
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState('');

  const [schemaFields, setSchemaFields] = useState<ReviewFieldDefinition[]>([]);
  const [requiredFields, setRequiredFields] = useState<string[]>([]);
  const [formMode, setFormMode] = useState<'visual' | 'json'>('visual');
  const [fullJsonText, setFullJsonText] = useState('');
  const [fullJsonError, setFullJsonError] = useState('');

  const defaultValues: ReviewConfigFormValues = {
    name: initialValues?.name ?? '',
    prompt: initialValues?.prompt ?? '',
    max_retries: initialValues?.max_retries ?? 20,
    model_name: initialValues?.model_name ?? 'gemini-2.5-pro',
    review_input_mode: initialValues?.review_input_mode ?? 'TRANSCRIPT',
    temperature: initialValues?.temperature ?? 0.2,
    is_default: initialValues?.is_default ?? false,
    notification_emails: initialValues?.notification_emails ?? [],
    high_intent_emails: initialValues?.high_intent_emails ?? [],
    review_date: initialValues?.review_date ?? null,
    transcript_language: initialValues?.transcript_language ?? null,
    translation_model: initialValues?.translation_model ?? 'gemini-2.5-flash',
  };

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    reset,
    watch,
    getValues,
    formState: { errors },
  } = useForm<ReviewConfigFormValues>({
    resolver: zodResolver(reviewConfigFormSchema),
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  // eslint-disable-next-line react-hooks/incompatible-library -- watch() is needed for reactive form field subscriptions
  const notificationEmails = watch('notification_emails');
  const highIntentEmails = watch('high_intent_emails');
  const isDefault = watch('is_default');

  // Check if should_send_high_intent_email exists in schema
  const hasHighIntentField = (() => {
    if (formMode === 'json') {
      try {
        const parsed = JSON.parse(fullJsonText || '{}');
        const schema = parsed?.response_fields_schema;
        return (
          !!schema?.properties?.should_send_high_intent_email ||
          (Array.isArray(schema?.required) &&
            schema.required.includes('should_send_high_intent_email'))
        );
      } catch {
        return fullJsonText.includes('"should_send_high_intent_email"');
      }
    }

    return schemaFields.some((f) => f.name === 'should_send_high_intent_email');
  })();
  // Initialize schema fields from initialValues
  useEffect(() => {
    if (initialValues?.response_fields_schema) {
      const converted = convertSchemaToFields(initialValues.response_fields_schema);
      setSchemaFields(converted);
      setRequiredFields(initialValues.response_fields_schema.required || []);
    }
  }, [initialValues]);

  // Merge API errors when they change
  useEffect(() => {
    if (apiErrors.length > 0) {
      mergeApiErrors(apiErrors, setError);
    }
  }, [apiErrors, setError]);

  const handleFormModeChange = (newMode: 'visual' | 'json') => {
    if (newMode === formMode) return;

    if (newMode === 'json') {
      // Visual → JSON: serialize current form state into full config JSON
      const formValues = getValues();
      const fullConfig = {
        name: formValues.name,
        prompt: formValues.prompt,
        max_retries: formValues.max_retries,
        model_name: formValues.model_name,
        review_input_mode: formValues.review_input_mode,
        temperature: formValues.temperature,
        is_default: formValues.is_default,
        notification_emails: formValues.notification_emails,
        high_intent_emails: formValues.high_intent_emails,
        response_fields_schema: convertFieldsToSchema(schemaFields, requiredFields) ?? {
          type: 'object',
          properties: {},
          required: [],
        },
        review_date: formValues.review_date || null,
        transcript_language: formValues.transcript_language || null,
        translation_model: formValues.translation_model,
      };
      setFullJsonText(JSON.stringify(fullConfig, null, 2));
      setFullJsonError('');
    } else {
      // JSON → Visual: parse JSON and populate form + schema editor
      try {
        const parsed = JSON.parse(fullJsonText);
        if (!parsed || typeof parsed !== 'object') {
          setFullJsonError('Must be a JSON object');
          return;
        }
        reset({
          name: parsed.name ?? '',
          prompt: parsed.prompt ?? '',
          max_retries: parsed.max_retries ?? 20,
          model_name: parsed.model_name ?? 'gemini-2.5-pro',
          review_input_mode: parsed.review_input_mode ?? 'TRANSCRIPT',
          temperature: parsed.temperature ?? 0.2,
          is_default: parsed.is_default ?? false,
          notification_emails: parsed.notification_emails ?? [],
          high_intent_emails: parsed.high_intent_emails ?? [],
          review_date: parsed.review_date ?? null,
          transcript_language: parsed.transcript_language ?? null,
          translation_model: parsed.translation_model ?? 'gemini-2.5-flash',
        });
        if (parsed.response_fields_schema) {
          const converted = convertSchemaToFields(parsed.response_fields_schema);
          setSchemaFields(converted);
          setRequiredFields(parsed.response_fields_schema.required || []);
        }
        setFullJsonError('');
      } catch {
        setFullJsonError('Invalid JSON — fix errors before switching to visual mode');
        return;
      }
    }
    setFormMode(newMode);
  };

  const handleFullJsonChange = (json: string) => {
    setFullJsonText(json);
    try {
      const parsed = JSON.parse(json);
      if (!parsed || typeof parsed !== 'object') {
        setFullJsonError('Must be a JSON object');
      } else {
        setFullJsonError('');
      }
    } catch {
      setFullJsonError('Invalid JSON');
    }
  };

  const getEffectiveSchema = () => convertFieldsToSchema(schemaFields, requiredFields);

  const handleVisualSubmit = handleSubmit(
    (data) => {
      const dto: CreateReviewConfigDto = {
        name: data.name,
        prompt: data.prompt,
        max_retries: data.max_retries,
        model_name: data.model_name,
        review_input_mode: data.review_input_mode,
        temperature: data.temperature,
        is_default: data.is_default,
        notification_emails: data.notification_emails,
        high_intent_emails: hasHighIntentField ? data.high_intent_emails : [],
        response_fields_schema: getEffectiveSchema(),
        review_date: data.review_date || null,
        transcript_language: data.transcript_language || null,
        translation_model: data.translation_model,
      };
      onSubmit?.(dto);
    },
    (formErrors) => {
      scrollToFirstError(formErrors);
    },
  );

  const handleJsonSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (fullJsonError) return;
    try {
      const parsed = JSON.parse(fullJsonText);
      if (!parsed.name || typeof parsed.name !== 'string') {
        setFullJsonError('Config must have a "name" string');
        return;
      }
      if (!parsed.prompt || typeof parsed.prompt !== 'string') {
        setFullJsonError('Config must have a "prompt" string');
        return;
      }
      const dto: CreateReviewConfigDto = {
        name: parsed.name,
        prompt: parsed.prompt,
        max_retries: parsed.max_retries ?? 20,
        model_name: parsed.model_name ?? 'gemini-2.5-pro',
        review_input_mode: parsed.review_input_mode ?? 'TRANSCRIPT',
        temperature: parsed.temperature ?? 0.2,
        is_default: parsed.is_default ?? false,
        notification_emails: parsed.notification_emails ?? [],
        high_intent_emails: parsed.high_intent_emails ?? [],
        response_fields_schema: parsed.response_fields_schema,
        review_date: parsed.review_date || null,
        transcript_language: parsed.transcript_language || null,
        translation_model: parsed.translation_model ?? 'gemini-2.5-flash',
      };
      onSubmit?.(dto);
    } catch {
      setFullJsonError('Invalid JSON');
    }
  };

  const onFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (formMode === 'json') {
      handleJsonSubmit(e);
    } else {
      handleVisualSubmit(e);
    }
  };

  const handleReset = () => {
    reset(defaultValues);
    if (initialValues?.response_fields_schema) {
      const converted = convertSchemaToFields(initialValues.response_fields_schema);
      setSchemaFields(converted);
      setRequiredFields(initialValues.response_fields_schema.required || []);
    } else {
      setSchemaFields([]);
      setRequiredFields([]);
    }
    setFullJsonText('');
    setFullJsonError('');
    setFormMode('visual');
  };

  const handleAddEmail = () => {
    setEmailError('');
    const email = emailInput.trim();

    if (!email) {
      setEmailError('Email is required');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError('Invalid email format');
      return;
    }

    if (notificationEmails.includes(email)) {
      setEmailError('Email already added');
      return;
    }

    setValue('notification_emails', [...notificationEmails, email]);
    setEmailInput('');
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    setValue(
      'notification_emails',
      notificationEmails.filter((email) => email !== emailToRemove),
    );
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleHighIntentEmailsChange = (emails: string[]) => {
    setValue('high_intent_emails', emails, { shouldDirty: true, shouldValidate: true });
  };

  return (
    <form className="space-y-5" onSubmit={onFormSubmit}>
      {/* Form Mode Toggle */}
      <div className="flex items-center justify-between">
        <div className="bg-muted inline-flex rounded-lg p-1">
          <Button
            type="button"
            onClick={() => handleFormModeChange('visual')}
            size="sm"
            variant={formMode === 'visual' ? 'default' : 'ghost'}
            className="h-7 px-3 text-xs"
            aria-pressed={formMode === 'visual'}
          >
            <Layers size={14} className="mr-1" />
            Visual
          </Button>
          <Button
            type="button"
            onClick={() => handleFormModeChange('json')}
            size="sm"
            variant={formMode === 'json' ? 'default' : 'ghost'}
            className="h-7 px-3 text-xs"
            aria-pressed={formMode === 'json'}
          >
            <Code size={14} className="mr-1" />
            JSON
          </Button>
        </div>
      </div>

      {formMode === 'json' ? (
        <div className="space-y-2">
          <p className="text-muted-foreground text-xs">
            Edit the complete review configuration as JSON. All fields including name, prompt, model
            settings, emails, and response schema are included.
          </p>
          <textarea
            value={fullJsonText}
            onChange={(e) => handleFullJsonChange(e.target.value)}
            rows={24}
            className={`border-input bg-background placeholder:text-muted-foreground focus-visible:ring-ring w-full rounded-md border px-3 py-2 font-mono text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none ${
              fullJsonError ? 'border-destructive' : ''
            }`}
            spellCheck={false}
            aria-label="Full configuration JSON"
            aria-invalid={!!fullJsonError}
            aria-describedby={fullJsonError ? 'full-json-error' : undefined}
          />
          {fullJsonError && (
            <p id="full-json-error" className="text-destructive text-sm" role="alert">
              {fullJsonError}
            </p>
          )}
        </div>
      ) : (
        <>
          {/* Section 1: General */}
          <section
            className="bg-card/50 space-y-4 rounded-xl border p-5"
            role="group"
            aria-labelledby="section-general"
          >
            <div className="border-b pb-3">
              <div className="flex items-center gap-2">
                <Settings2 className="text-muted-foreground h-5 w-5" />
                <h3 id="section-general" className="text-base font-semibold">
                  General
                </h3>
              </div>
              <p className="text-muted-foreground mt-1 text-xs">
                Configuration name, retry limits, and default settings.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                label="Configuration name"
                htmlFor="name"
                error={errors.name?.message}
                required
              >
                <FormInput
                  id="name"
                  placeholder="e.g. Default Review Config"
                  error={!!errors.name}
                  {...register('name')}
                />
              </FormField>

              <FormField
                label="Max retries"
                htmlFor="max_retries"
                error={errors.max_retries?.message}
                required
              >
                <FormInput
                  id="max_retries"
                  type="number"
                  min={1}
                  max={100}
                  error={!!errors.max_retries}
                  {...register('max_retries', { valueAsNumber: true })}
                />
              </FormField>
            </div>

            <div className="flex items-center justify-between rounded-lg border px-4 py-3">
              <div>
                <label htmlFor="is_default" className="text-sm font-medium">
                  Default configuration
                </label>
                <p className="text-muted-foreground text-xs">
                  Used when no specific configuration is selected
                </p>
              </div>
              <Switch
                id="is_default"
                checked={isDefault}
                onCheckedChange={(checked) => setValue('is_default', checked)}
                aria-label="Set as default review configuration"
              />
            </div>
          </section>

          {/* Section 2: AI Model Settings */}
          <section
            className="bg-card/50 space-y-4 rounded-xl border p-5"
            role="group"
            aria-labelledby="section-model"
          >
            <div className="border-b pb-3">
              <div className="flex items-center gap-2">
                <Cpu className="text-muted-foreground h-5 w-5" />
                <h3 id="section-model" className="text-base font-semibold">
                  AI Model Settings
                </h3>
              </div>
              <p className="text-muted-foreground mt-1 text-xs">
                Model selection, input mode, and generation parameters.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                label="Model"
                htmlFor="model_name"
                error={errors.model_name?.message}
                required
              >
                <FormSelect id="model_name" error={!!errors.model_name} {...register('model_name')}>
                  {GEMINI_MODELS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </FormSelect>
              </FormField>

              <FormField
                label="Review Input"
                htmlFor="review_input_mode"
                error={errors.review_input_mode?.message}
                required
                description="Audio mode reviews directly from GCS recording and uses transcript fallback if needed."
              >
                <FormSelect
                  id="review_input_mode"
                  error={!!errors.review_input_mode}
                  {...register('review_input_mode')}
                >
                  {REVIEW_INPUT_MODES.map((mode) => (
                    <option key={mode.value} value={mode.value}>
                      {mode.label}
                    </option>
                  ))}
                </FormSelect>
              </FormField>

              <FormField
                label="Temperature"
                htmlFor="temperature"
                error={errors.temperature?.message}
                required
              >
                <FormInput
                  id="temperature"
                  type="number"
                  min={0}
                  max={2}
                  step={0.1}
                  error={!!errors.temperature}
                  {...register('temperature', { valueAsNumber: true })}
                />
              </FormField>

              <FormField
                label="Translate transcript to"
                htmlFor="transcript_language"
                error={errors.transcript_language?.message}
                description="Calls in another language get an extra translated transcript. The original is always kept."
              >
                <FormSelect
                  id="transcript_language"
                  error={!!errors.transcript_language}
                  {...register('transcript_language', {
                    setValueAs: (v) => v || null,
                  })}
                >
                  {TRANSCRIPT_LANGUAGES.map((lang) => (
                    <option key={lang.value} value={lang.value}>
                      {lang.label}
                    </option>
                  ))}
                </FormSelect>
              </FormField>

              <FormField
                label="Translation model"
                htmlFor="translation_model"
                error={errors.translation_model?.message}
                description="Model used for transcript translation. Flash is fast and cheap; only raise it if translations miss nuance."
              >
                <FormSelect
                  id="translation_model"
                  error={!!errors.translation_model}
                  {...register('translation_model')}
                >
                  {GEMINI_MODELS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </FormSelect>
              </FormField>

              <FormField
                label="Review date (optional)"
                htmlFor="review_date"
                error={errors.review_date?.message}
                description="Override today's date for reviewing past calls. Leave empty to use current date."
              >
                <FormInput
                  id="review_date"
                  type="date"
                  error={!!errors.review_date}
                  {...register('review_date', {
                    setValueAs: (v) => v || null,
                  })}
                />
              </FormField>
            </div>
          </section>

          {/* Section 3: Review Prompt */}
          <section
            className="bg-card/50 space-y-4 rounded-xl border p-5"
            role="group"
            aria-labelledby="section-prompt"
          >
            <div className="border-b pb-3">
              <div className="flex items-center gap-2">
                <FileText className="text-muted-foreground h-5 w-5" />
                <h3 id="section-prompt" className="text-base font-semibold">
                  Review Prompt
                </h3>
              </div>
              <p className="text-muted-foreground mt-1 text-xs">
                The instructions sent to the AI model for reviewing calls.
              </p>
            </div>

            <FormField
              label="Prompt"
              htmlFor="prompt"
              error={errors.prompt?.message}
              required
              description="Use {{TRANSCRIPT}}, {{ORIGINAL_OUTCOME}}, {{ORIGINAL_AVAILABILITY}}, {{ORIGINAL_LEAD_DATA}} as placeholders"
            >
              <FormTextarea
                id="prompt"
                rows={12}
                placeholder="You are an AI agent that reviews customer service call recordings..."
                error={!!errors.prompt}
                {...register('prompt')}
              />
            </FormField>
          </section>

          {/* Section 4: Notifications */}
          <section
            className="bg-card/50 space-y-4 rounded-xl border p-5"
            role="group"
            aria-labelledby="section-notifications"
          >
            <div className="border-b pb-3">
              <div className="flex items-center gap-2">
                <Mail className="text-muted-foreground h-5 w-5" />
                <h3 id="section-notifications" className="text-base font-semibold">
                  Notifications
                </h3>
              </div>
              <p className="text-muted-foreground mt-1 text-xs">
                Email recipients for review completion alerts.
              </p>
            </div>

            <FormField
              label="Review notifications"
              htmlFor="notification_emails"
              error={errors.notification_emails?.message}
              description="Emails to notify when a review completes with issues"
            >
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={emailInput}
                    onChange={(e) => {
                      setEmailInput(e.target.value);
                      setEmailError('');
                    }}
                    onKeyDown={handleEmailKeyDown}
                    placeholder="admin@example.com"
                    aria-describedby="notification-emails-desc"
                    className="border-input bg-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                  />
                  <Button type="button" variant="outline" onClick={handleAddEmail}>
                    <Plus className="h-4 w-4" />
                    Add
                  </Button>
                </div>
                {emailError && <p className="text-destructive text-sm">{emailError}</p>}
                {notificationEmails.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {notificationEmails.map((email) => (
                      <Badge key={email} variant="secondary" className="gap-1">
                        {email}
                        <button
                          type="button"
                          onClick={() => handleRemoveEmail(email)}
                          className="hover:text-destructive ml-1"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </FormField>
          </section>

          {/* Section 5: Response Schema */}
          <section
            className="bg-card/50 space-y-4 rounded-xl border p-5"
            role="group"
            aria-labelledby="section-schema"
          >
            <div className="border-b pb-3">
              <div className="flex items-center gap-2">
                <Layers className="text-muted-foreground h-5 w-5" />
                <h3 id="section-schema" className="text-base font-semibold">
                  Response Schema
                </h3>
              </div>
              <p className="text-muted-foreground mt-1 text-xs">
                Define the structure of data the AI should extract from call transcripts.
              </p>
            </div>

            <ReviewFieldsSchemaEditor
              value={schemaFields}
              onChange={setSchemaFields}
              requiredFields={requiredFields}
              onRequiredFieldsChange={setRequiredFields}
              highIntentEmails={highIntentEmails}
              onHighIntentEmailsChange={handleHighIntentEmailsChange}
            />
          </section>
        </>
      )}

      {/* Sticky footer */}
      <div className="bg-background sticky bottom-0 border-t pt-4 pb-1">
        <div className="flex items-center justify-end gap-3">
          <Button type="button" variant="outline" onClick={handleReset}>
            <RotateCw className="h-4 w-4" />
            Reset
          </Button>
          <Button type="submit" disabled={isLoading}>
            <Save className="h-4 w-4" />
            {submitLabel}
          </Button>
        </div>
      </div>
    </form>
  );
};
